import { getExtraHeaders, apiRequest } from "@/lib/queryClient";

/**
 * Typed client for the metrics console API (Baremetrics for EduFocal Academy).
 * Shapes follow scripts/metrics/API_CONTRACT.md exactly.
 *
 * Money is JMD cents unless a field name ends in `Usd` (USD cents).
 * Dates are `YYYY-MM-DD` in Jamaica time.
 */

export const METRICS_BASE = "/api/admin/metrics";

// ─────────────────────────────────────────────────────────────────────────────
// Shapes
// ─────────────────────────────────────────────────────────────────────────────

export type Granularity = "day" | "week" | "month";
export type MetricUnit = "money" | "count" | "percent" | "ratio";
export type MetricGroup = "revenue" | "customers" | "movements" | "health" | "trials" | "collections";
export type AnnotationKind = "note" | "campaign" | "pricing" | "product" | "exam" | "incident";
export type CustomerStatus = "all" | "active" | "churned" | "past_due" | "trialing" | "paused";
export type TimelineKind =
  | "new" | "reactivation" | "expansion" | "contraction" | "churn"
  | "trial_start" | "trial_end_no_convert"
  | "payment" | "payment_failed" | "payment_pending"
  | "refund" | "exit_survey" | "recovery";

export interface SeriesPoint { date: string; label: string; value: number }

export interface MetricCardData {
  id: string;
  label: string;
  unit: MetricUnit;
  group: MetricGroup;
  description: string;
  value: number;
  previous: number | null;
  change: number | null;
  changePct: number | null;
  spark: SeriesPoint[];
}

export interface MetricDef {
  id: string;
  label: string;
  unit: MetricUnit;
  kind: string;
  group: MetricGroup;
  description: string;
}

export interface Annotation {
  id: string;
  date: string;
  title: string;
  note: string | null;
  kind: AnnotationKind;
  auto: boolean;
}

export interface Goal {
  id: string;
  metric: string;
  label: string;
  unit: MetricUnit;
  lowerIsBetter: boolean;
  target: number;
  startValue: number;
  value: number;
  /** 0-100 */
  progress: number;
  achieved: boolean;
  achievedAt: string | null;
  daysLeft: number;
  onTrack: boolean;
  startsAt: string;
  dueAt: string;
}

export interface SyncStatus {
  lastFullAt: string | null;
  lastIncrementalAt: string | null;
  counts: { subscriptions: number; invoices: number; charges: number; refunds: number };
  lastError: string | null;
  running: boolean;
  events?: number;
}

export interface OverviewResponse {
  from: string;
  to: string;
  previous: { from: string; to: string };
  granularity: Granularity;
  cards: MetricCardData[];
  extras: { netRevenueUsd: number; paused: number; disputes: number };
  goals: Goal[];
  annotations: Annotation[];
}

export interface MetricsCatalogue { metrics: MetricDef[] }

export interface SeriesResponse {
  metric: string;
  granularity: Granularity;
  points: SeriesPoint[];
  annotations: Annotation[];
}

export interface DrilldownRow {
  customerId: string;
  userId: string | null;
  name: string | null;
  email: string | null;
  grade: number | null;
  plan: string | null;
  interval: string | null;
  status: string | null;
  mrr: number | null;
  at: string;
  delta: number | null;
  type: string | null;
  reason: string | null;
  amount?: number | null;
}

export interface DrilldownResponse { metric: string; rows: DrilldownRow[] }

export interface MovementRow {
  month: string;
  mrrStart: number;
  new: number;
  expansion: number;
  reactivation: number;
  contraction: number;
  churn: number;
  netNew: number;
  mrrEnd: number;
  newCustomers: number;
  reactivated: number;
  upgraded: number;
  downgraded: number;
  cancelled: number;
  customersEnd: number;
}

export interface MovementsResponse { rows: MovementRow[] }

export interface SegmentOptions {
  grades: number[];
  plans: Array<{ plan: string; n: number }>;
  sources: Array<{ source: string; n: number }>;
  intervals: string[];
}

export interface CustomerRow {
  customerId: string;
  userId: string | null;
  name: string | null;
  email: string | null;
  grade: number | null;
  status: string;
  plan: string | null;
  interval: string | null;
  mrr: number;
  ltv: number;
  since: string | null;
  lastPaymentAt: string | null;
  failedCharges: number;
  subscriptions: number;
}

export interface CustomersResponse {
  rows: CustomerRow[];
  total: number;
  page: number;
  pageSize: number;
}

export interface TimelineEntry {
  at: string;
  kind: TimelineKind | string;
  title: string;
  detail?: string | null;
  amount?: number | null;
  currency?: string | null;
  status?: string | null;
  url?: string | null;
}

/** The contract leaves invoice/charge/refund rows as Stripe mirrors, so read them defensively. */
export interface MoneyRow {
  id?: string;
  number?: string | null;
  at?: string | null;
  created?: string | null;
  date?: string | null;
  amount?: number | null;
  amountDue?: number | null;
  amountPaid?: number | null;
  total?: number | null;
  currency?: string | null;
  status?: string | null;
  reason?: string | null;
  description?: string | null;
  failureCode?: string | null;
  failureMessage?: string | null;
  hostedInvoiceUrl?: string | null;
  invoicePdf?: string | null;
  receiptUrl?: string | null;
  url?: string | null;
  [key: string]: unknown;
}

export interface CustomerSubscriptionRow {
  id?: string;
  subscriptionId?: string;
  status?: string | null;
  plan?: string | null;
  interval?: string | null;
  mrr?: number | null;
  amount?: number | null;
  currentPeriodEnd?: string | null;
  periodEnd?: string | null;
  cancelAtPeriodEnd?: boolean | null;
  trialEnd?: string | null;
  startedAt?: string | null;
  createdAt?: string | null;
  [key: string]: unknown;
}

export interface CustomerDetail {
  customerId: string;
  user: {
    id?: string;
    name?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
    grade?: number | null;
    [key: string]: unknown;
  } | null;
  mrr: number;
  ltv: number;
  currentMrrByPrice: Array<{ id: string; mrr: number; plan: string | null; interval: string | null; status: string | null }>;
  subscriptions: CustomerSubscriptionRow[];
  invoices: MoneyRow[];
  charges: MoneyRow[];
  refunds: MoneyRow[];
  events: Array<{ at: string; type: string; delta: number; reason?: string | null; [key: string]: unknown }>;
  feedback: Array<{ at?: string | null; reason?: string | null; details?: string | null; [key: string]: unknown }>;
  recoveryCases: RecoverCase[];
  timeline: TimelineEntry[];
  stats: { payments: number; failed: number; refunded: number; months: number };
}

export interface TrialsResponse {
  cohorts: Array<{ month: string; started: number; converted: number; stillTrialing: number; rate: number; avgDaysToConvert: number | null }>;
  totals: { started: number; converted: number; rate: number };
  active: Array<{
    subscriptionId: string;
    customerId: string;
    userId: string | null;
    name: string | null;
    email: string | null;
    grade: number | null;
    plan: string | null;
    interval: string | null;
    trialEnd: string;
    daysLeft: number;
    lastActiveAt: string | null;
  }>;
}

export interface CancellationsResponse {
  from: string;
  to: string;
  total: number;
  mrrLost: number;
  avgMonthsSubscribed: number;
  reasons: Array<{ reason: string; count: number; share: number; mrrLost: number; avgMonths: number }>;
  stripeReasons: Array<{ reason: string; count: number }>;
  offers: { shown: number; accepted: number; saveRate: number };
  monthly: Array<{ month: string; cancellations: number; mrrLost: number; saves: number }>;
  tenure: Array<{ bucket: string; count: number }>;
  recent: Array<{
    at: string;
    userId: string | null;
    name: string | null;
    email: string | null;
    grade: number | null;
    plan: string | null;
    reason: string | null;
    details: string | null;
    mrrLost: number;
    months: number;
    offerShown: boolean;
    offerAccepted: boolean;
  }>;
}

export type ForecastScenario = "base" | "optimistic" | "pessimistic";

export interface ForecastResponse {
  scenario: ForecastScenario;
  assumptions: {
    newMrrPerMonth: number;
    newCustomersPerMonth: number;
    /** 0-1 */
    revenueChurnRate: number;
    /** 0-1 */
    customerChurnRate: number;
    /** 0-1 */
    expansionRate: number;
    /** 0-1 */
    contractionRate: number;
    seasonality: Record<string, number>;
    basedOnMonths: number;
  };
  months: Array<{
    month: string;
    mrr: number;
    customers: number;
    newMrr: number;
    churnMrr: number;
    expansionMrr: number;
    contractionMrr: number;
    newCustomers: number;
    churnedCustomers: number;
  }>;
  history: Array<{ month: string; mrr: number; customers: number }>;
}

export interface RecoverReminder { at: string; channel: string; step: number | string; ok: boolean; error?: string | null }

export interface RecoverCase {
  id: string;
  invoiceId: string | null;
  subscriptionId: string | null;
  customerId: string | null;
  userId: string | null;
  name: string | null;
  email: string | null;
  parentEmail: string | null;
  parentPhone: string | null;
  grade: number | null;
  amount: number;
  currency: string | null;
  failureCode: string | null;
  failureMessage: string | null;
  payUrl: string | null;
  status: string;
  openedAt: string;
  daysOpen: number;
  nextReminderAt: string | null;
  reminders: RecoverReminder[];
  recoveredAt: string | null;
  recoveredAmount: number | null;
  closedAt: string | null;
  closedReason: string | null;
}

export interface RecoverResponse {
  stats: {
    open: { count: number; amount: number };
    recovered30: { count: number; amount: number };
    lost30: { count: number; amount: number };
    recoveryRate90: number;
    avgDaysToRecover: number | null;
    byFailure: Array<{ code: string; count: number; amount: number; recovered: number }>;
    monthly: Array<{ month: string; opened: number; recovered: number; lost: number; recoveredAmount: number; atRisk: number }>;
  };
  cases: RecoverCase[];
  sequence: string[];
}

export interface GoalsResponse {
  goals: Goal[];
  metrics: Array<{ id: string; label: string; unit: MetricUnit; lowerIsBetter?: boolean }>;
}

export interface AnnotationsResponse { annotations: Annotation[] }

export interface MetricsSettings {
  digest: { enabled: boolean; emails: string[]; weekday: number; hour: number };
  telegramDaily: { enabled: boolean; hour: number };
  alerts: { enabled: boolean; mrrDropPct: number; failedChargesMultiplier: number; churnSpikeCount: number };
  currency: string;
}

export interface SettingsResponse { settings: MetricsSettings }

export interface DigestPreview { digest: unknown; html: string }

// ─────────────────────────────────────────────────────────────────────────────
// Segment + range params
// ─────────────────────────────────────────────────────────────────────────────

export interface Segment {
  interval?: string;
  grade?: string;
  plan?: string;
  source?: string;
  coupon?: string;
}

export const EMPTY_SEGMENT: Segment = {};

export interface RangeParams { from: string; to: string }
export type ScopedParams = RangeParams & Segment;

/** Human labels for the segment chips. */
export const SEGMENT_LABELS: Record<keyof Segment, string> = {
  interval: "Billing",
  grade: "Grade",
  plan: "Plan",
  source: "Source",
  coupon: "Coupon",
};

export function segmentValueLabel(key: keyof Segment, value: string): string {
  if (key === "grade") return `Grade ${value}`;
  if (key === "interval") return value === "month" ? "Monthly" : "Yearly";
  if (key === "coupon") return value === "true" ? "On a coupon" : "No coupon";
  return value;
}

export function activeSegmentEntries(segment: Segment): Array<[keyof Segment, string]> {
  return (Object.keys(SEGMENT_LABELS) as Array<keyof Segment>)
    .filter((k) => segment[k])
    .map((k) => [k, segment[k] as string]);
}

type QueryValue = string | number | boolean | null | undefined;

/** Deterministic query string so URLs stay stable as React Query keys. */
export function qs(params: object): string {
  const bag = params as Record<string, QueryValue>;
  const search = new URLSearchParams();
  for (const key of Object.keys(bag).sort()) {
    const value = bag[key];
    if (value === undefined || value === null || value === "") continue;
    search.set(key, String(value));
  }
  const out = search.toString();
  return out ? `?${out}` : "";
}

// ─────────────────────────────────────────────────────────────────────────────
// Dates (Jamaica time, no DST)
// ─────────────────────────────────────────────────────────────────────────────

const ISO_FMT = new Intl.DateTimeFormat("en-CA", {
  timeZone: "America/Jamaica",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

export function jamaicaToday(): string {
  return ISO_FMT.format(new Date());
}

function toUtc(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(Date.UTC(y, (m || 1) - 1, d || 1));
}

function fromUtc(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export function isoAddDays(iso: string, days: number): string {
  const d = toUtc(iso);
  d.setUTCDate(d.getUTCDate() + days);
  return fromUtc(d);
}

export function isoAddMonths(iso: string, months: number): string {
  const d = toUtc(iso);
  const day = d.getUTCDate();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() + months);
  const last = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
  d.setUTCDate(Math.min(day, last));
  return fromUtc(d);
}

export function daysInRange(from: string, to: string): number {
  return Math.round((toUtc(to).getTime() - toUtc(from).getTime()) / 86_400_000) + 1;
}

/** The period of the same length that ends the day before `from`. */
export function previousRange(from: string, to: string): RangeParams {
  const length = Math.max(1, daysInRange(from, to));
  const prevTo = isoAddDays(from, -1);
  return { from: isoAddDays(prevTo, -(length - 1)), to: prevTo };
}

export const RANGE_PRESETS = [
  { id: "7d", label: "Last 7 days" },
  { id: "30d", label: "Last 30 days" },
  { id: "90d", label: "Last 90 days" },
  { id: "mtd", label: "Month to date" },
  { id: "qtd", label: "Quarter to date" },
  { id: "ytd", label: "Year to date" },
  { id: "12m", label: "Last 12 months" },
  { id: "custom", label: "Custom range" },
] as const;

export type RangePreset = (typeof RANGE_PRESETS)[number]["id"];

export function resolvePreset(preset: RangePreset, today = jamaicaToday()): RangeParams {
  const d = toUtc(today);
  switch (preset) {
    case "7d": return { from: isoAddDays(today, -6), to: today };
    case "90d": return { from: isoAddDays(today, -89), to: today };
    case "mtd": return { from: `${today.slice(0, 7)}-01`, to: today };
    case "qtd": {
      const q = Math.floor(d.getUTCMonth() / 3) * 3;
      return { from: `${d.getUTCFullYear()}-${String(q + 1).padStart(2, "0")}-01`, to: today };
    }
    case "ytd": return { from: `${d.getUTCFullYear()}-01-01`, to: today };
    case "12m": return { from: isoAddDays(isoAddMonths(today, -12), 1), to: today };
    case "custom":
    case "30d":
    default: return { from: isoAddDays(today, -29), to: today };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Formatting
// ─────────────────────────────────────────────────────────────────────────────

/** JMD cents → `J$12,500`. */
export function formatMoney(cents: number | null | undefined): string {
  if (cents == null || !Number.isFinite(cents)) return "–";
  const sign = cents < 0 ? "-" : "";
  return `${sign}J$${Math.round(Math.abs(cents) / 100).toLocaleString("en-JM")}`;
}

/** Compact JMD for chart axes: `J$12.5k`. */
export function formatMoneyShort(cents: number | null | undefined): string {
  if (cents == null || !Number.isFinite(cents)) return "–";
  const v = Math.abs(cents) / 100;
  const sign = cents < 0 ? "-" : "";
  if (v >= 1_000_000) return `${sign}J$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1000) return `${sign}J$${(v / 1000).toFixed(v >= 100_000 ? 0 : 1)}k`;
  return `${sign}J$${Math.round(v)}`;
}

/** USD cents → `US$1,240` (only where a `Usd` field exists). */
export function formatUsd(cents: number | null | undefined): string {
  if (cents == null || !Number.isFinite(cents)) return "–";
  const sign = cents < 0 ? "-" : "";
  return `${sign}US$${Math.round(Math.abs(cents) / 100).toLocaleString("en-US")}`;
}

export function formatCount(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return "–";
  return Math.round(n).toLocaleString();
}

/** Percent metrics arrive as percentage numbers (4.2 = 4.2%). One decimal. */
export function formatPercent(v: number | null | undefined, decimals = 1): string {
  if (v == null || !Number.isFinite(v)) return "–";
  return `${v.toFixed(decimals)}%`;
}

/** Rate fields (recovery rate, save rate, share, trial rate) also arrive as percentage numbers. */
export function formatRate(v: number | null | undefined, decimals = 1): string {
  if (v == null || !Number.isFinite(v)) return "–";
  return `${v.toFixed(decimals)}%`;
}

export function formatRatio(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return "–";
  return v.toFixed(2);
}

export function formatMetricValue(value: number | null | undefined, unit: MetricUnit): string {
  switch (unit) {
    case "money": return formatMoney(value);
    case "percent": return formatPercent(value);
    case "ratio": return formatRatio(value);
    case "count":
    default: return formatCount(value);
  }
}

export function formatMetricShort(value: number | null | undefined, unit: MetricUnit): string {
  if (unit === "money") return formatMoneyShort(value);
  return formatMetricValue(value, unit);
}

/** Signed change, formatted in the metric's own unit. */
export function formatChange(change: number | null | undefined, unit: MetricUnit): string {
  if (change == null || !Number.isFinite(change)) return "–";
  const body = unit === "money"
    ? formatMoney(Math.abs(change))
    : unit === "percent"
      ? `${Math.abs(change).toFixed(1)}pp`
      : unit === "ratio"
        ? Math.abs(change).toFixed(2)
        : formatCount(Math.abs(change));
  return `${change > 0 ? "+" : change < 0 ? "−" : ""}${body}`;
}

export function formatChangePct(changePct: number | null | undefined): string | null {
  if (changePct == null || !Number.isFinite(changePct)) return null;
  return `${changePct > 0 ? "+" : changePct < 0 ? "−" : ""}${Math.abs(changePct).toFixed(1)}%`;
}

export function monthLabel(month: string): string {
  if (!month) return "–";
  const iso = month.length === 7 ? `${month}-15` : month;
  const d = toUtc(iso);
  return d.toLocaleDateString("en-US", { month: "short", year: "2-digit", timeZone: "UTC" });
}

export function shortDate(iso: string | null | undefined): string {
  if (!iso) return "–";
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00-05:00` : iso);
  if (Number.isNaN(d.getTime())) return "–";
  return d.toLocaleDateString("en-JM", { month: "short", day: "numeric", timeZone: "America/Jamaica" });
}

// ─────────────────────────────────────────────────────────────────────────────
// "Good direction": green when the change helps the business
// ─────────────────────────────────────────────────────────────────────────────

const LOWER_IS_BETTER = new Set([
  "fees", "refunds", "downgrades", "cancellations",
  "mrr_contraction", "mrr_churn",
  "customer_churn_rate", "revenue_churn_rate",
  "failed_charges", "failed_amount",
]);

export function metricLowerIsBetter(metricId: string): boolean {
  if (LOWER_IS_BETTER.has(metricId)) return true;
  return /churn|cancel|fail|refund|contraction|downgrade|(^|_)fees?($|_)/.test(metricId);
}

export type ChangeTone = "good" | "bad" | "flat";

export function changeTone(metricId: string, change: number | null | undefined): ChangeTone {
  if (change == null || !Number.isFinite(change) || change === 0) return "flat";
  const better = metricLowerIsBetter(metricId) ? change < 0 : change > 0;
  return better ? "good" : "bad";
}

export const GROUP_ORDER: MetricGroup[] = ["revenue", "customers", "movements", "health", "trials", "collections"];

export const GROUP_LABELS: Record<MetricGroup, string> = {
  revenue: "Revenue",
  customers: "Customers",
  movements: "Movements",
  health: "Health",
  trials: "Trials",
  collections: "Collections",
};

// ─────────────────────────────────────────────────────────────────────────────
// URL builders (also the React Query keys)
// ─────────────────────────────────────────────────────────────────────────────

export const metricsUrls = {
  syncStatus: () => `${METRICS_BASE}/sync/status`,
  overview: (p: ScopedParams & { compare?: boolean }) =>
    `${METRICS_BASE}/overview${qs({ ...p, compare: p.compare ? 1 : undefined })}`,
  metrics: () => `${METRICS_BASE}/metrics`,
  series: (p: ScopedParams & { metric: string; granularity?: Granularity | "auto" }) =>
    `${METRICS_BASE}/series${qs({ ...p, granularity: p.granularity === "auto" ? undefined : p.granularity })}`,
  drilldown: (p: ScopedParams & { metric: string }) => `${METRICS_BASE}/drilldown${qs(p)}`,
  movements: (p: Segment & { months: number }) => `${METRICS_BASE}/movements${qs(p)}`,
  segmentOptions: () => `${METRICS_BASE}/segments/options`,
  exportCsv: (p: ScopedParams & { metric: string; granularity?: Granularity | "auto" }) =>
    `${METRICS_BASE}/export.csv${qs({ ...p, granularity: p.granularity === "auto" ? undefined : p.granularity })}`,
  customers: (p: { q?: string; status?: CustomerStatus; sort?: string; dir?: string; page?: number; pageSize?: number }) =>
    `${METRICS_BASE}/customers${qs(p)}`,
  customer: (customerId: string) => `${METRICS_BASE}/customers/${encodeURIComponent(customerId)}`,
  customerByUser: (userId: string) => `${METRICS_BASE}/customers/by-user/${encodeURIComponent(userId)}`,
  trials: (p: { months: number }) => `${METRICS_BASE}/trials${qs(p)}`,
  cancellations: (p: RangeParams) => `${METRICS_BASE}/cancellations${qs(p)}`,
  forecast: (p: Segment & {
    months: number;
    scenario: ForecastScenario;
    newMrrPerMonth?: number;
    newCustomersPerMonth?: number;
    revenueChurnRate?: number;
    customerChurnRate?: number;
  }) => `${METRICS_BASE}/forecast${qs(p)}`,
  recover: (p: { status: string }) => `${METRICS_BASE}/recover${qs(p)}`,
  goals: () => `${METRICS_BASE}/goals`,
  annotations: (p: RangeParams) => `${METRICS_BASE}/annotations${qs(p)}`,
  settings: () => `${METRICS_BASE}/settings`,
};

// ─────────────────────────────────────────────────────────────────────────────
// Fetchers
// ─────────────────────────────────────────────────────────────────────────────

export async function fetchJson<T>(url: string): Promise<T> {
  const res = await fetch(url, { credentials: "include", headers: getExtraHeaders() });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    let message = text || `Request failed (${res.status})`;
    try {
      const parsed = JSON.parse(text);
      message = parsed.error || parsed.message || message;
    } catch {
      /* not JSON, keep the raw text */
    }
    throw new Error(message);
  }
  return (await res.json()) as T;
}

async function send<T>(method: string, url: string, body?: unknown): Promise<T> {
  const res = await apiRequest(method, url, body);
  return (await res.json()) as T;
}

// Status / sync
export const getSyncStatus = () => fetchJson<SyncStatus>(metricsUrls.syncStatus());
export const runSync = (full = false) => send<SyncStatus>("POST", `${METRICS_BASE}/sync`, { full });

// Control center
export const getOverview = (p: ScopedParams & { compare?: boolean }) => fetchJson<OverviewResponse>(metricsUrls.overview(p));
export const getMetricCatalogue = () => fetchJson<MetricsCatalogue>(metricsUrls.metrics());
export const getSeries = (p: ScopedParams & { metric: string; granularity?: Granularity | "auto" }) =>
  fetchJson<SeriesResponse>(metricsUrls.series(p));
export const getDrilldown = (p: ScopedParams & { metric: string }) => fetchJson<DrilldownResponse>(metricsUrls.drilldown(p));
export const getMovements = (p: Segment & { months: number }) => fetchJson<MovementsResponse>(metricsUrls.movements(p));
export const getSegmentOptions = () => fetchJson<SegmentOptions>(metricsUrls.segmentOptions());

// Customers
export const getCustomers = (p: Parameters<typeof metricsUrls.customers>[0]) => fetchJson<CustomersResponse>(metricsUrls.customers(p));
export const getCustomer = (customerId: string) => fetchJson<CustomerDetail>(metricsUrls.customer(customerId));
export const getCustomerByUser = (userId: string) => fetchJson<{ customerId: string }>(metricsUrls.customerByUser(userId));

// Trials / cancellations / forecast
export const getTrials = (months = 12) => fetchJson<TrialsResponse>(metricsUrls.trials({ months }));
export const getCancellations = (p: RangeParams) => fetchJson<CancellationsResponse>(metricsUrls.cancellations(p));
export const getForecast = (p: Parameters<typeof metricsUrls.forecast>[0]) => fetchJson<ForecastResponse>(metricsUrls.forecast(p));

// Recover
export const getRecover = (status: string) => fetchJson<RecoverResponse>(metricsUrls.recover({ status }));
export const syncRecoverCases = () => send<{ opened: number; recovered: number; lost: number }>("POST", `${METRICS_BASE}/recover/sync`);
export const sendRecoverLink = (id: string, channels: string[]) =>
  send<{ email?: { ok: boolean; error?: string }; whatsapp?: { ok: boolean; error?: string } }>(
    "POST", `${METRICS_BASE}/recover/${encodeURIComponent(id)}/send-link`, { channels },
  );
export const retryRecoverCharge = (id: string) =>
  send<{ ok: boolean; status?: string; error?: string }>("POST", `${METRICS_BASE}/recover/${encodeURIComponent(id)}/retry`);
export const closeRecoverCase = (id: string, reason: string) =>
  send<{ ok: boolean }>("POST", `${METRICS_BASE}/recover/${encodeURIComponent(id)}/close`, { reason });

// Goals & annotations
export const getGoals = () => fetchJson<GoalsResponse>(metricsUrls.goals());
export const createGoal = (body: { metric: string; target: number; dueAt: string; label?: string }) =>
  send<{ goal: Goal }>("POST", `${METRICS_BASE}/goals`, body);
export const deleteGoal = (id: string) => send<{ ok: boolean }>("DELETE", `${METRICS_BASE}/goals/${encodeURIComponent(id)}`);
export const getAnnotations = (p: RangeParams) => fetchJson<AnnotationsResponse>(metricsUrls.annotations(p));
export const createAnnotation = (body: { date: string; title: string; note?: string; kind?: AnnotationKind }) =>
  send<{ annotation: Annotation }>("POST", `${METRICS_BASE}/annotations`, body);
export const deleteAnnotation = (id: string) =>
  send<{ ok: boolean }>("DELETE", `${METRICS_BASE}/annotations/${encodeURIComponent(id)}`);

// Settings & reports
export const getSettings = () => fetchJson<SettingsResponse>(metricsUrls.settings());
export const updateSettings = (body: Partial<MetricsSettings>) => send<SettingsResponse>("PUT", metricsUrls.settings(), body);
export const previewDigest = () => send<DigestPreview>("POST", `${METRICS_BASE}/digest/preview`);
export const sendDigest = (to?: string[]) =>
  send<{ sent: string[]; telegram: unknown }>("POST", `${METRICS_BASE}/digest/send`, to && to.length ? { to } : {});
export const runAlerts = () => send<{ fired: string[] }>("POST", `${METRICS_BASE}/alerts/run`);

export const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

export function hourLabel(hour: number): string {
  const h = ((hour % 24) + 24) % 24;
  const suffix = h < 12 ? "am" : "pm";
  const display = h % 12 === 0 ? 12 : h % 12;
  return `${display}:00 ${suffix}`;
}

export function errorMessage(error: unknown): string {
  return (error as Error)?.message || "Something went wrong";
}
